package com.wintermar.inspection.feature.presentation.inspection.complete

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.widget.LoadingDialog
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.model.template.TemplateOptionModel
import com.wintermar.inspection.feature.presentation.inspection.complete.widget.InspectionCompleteContent
import com.wintermar.inspection.feature.presentation.inspection.complete.widget.confirm_approval.ConfirmApprovalModal
import com.wintermar.inspection.feature.presentation.inspection.complete.widget.header.Header
import com.wintermar.inspection.feature.presentation.inspection.complete.widget.page_info.PageInfoModal
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.launch
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun InspectionCompleteScreen(
    paddingValues: PaddingValues,
    data: InspectionModel,
    templateOptions: List<TemplateOptionModel>,
    viewModel: InspectionCompleteViewModel = koinViewModel { parametersOf(data.id) },
    onBack: () -> Unit,
    onEdit: () -> Unit,
    onApproved: (inspectionId: String) -> Unit
) {
    val state = viewModel.state
    val color = LocalAppTheme.current.color
    val flaggedItems = data.template.getFlaggedAnswer()
    val pages = flaggedItems.groupBy { it.pageTitle }.map { it.key }
    var showPageModal by remember { mutableStateOf(false) }
    var showApprovalModal by remember { mutableStateOf(false) }
    val listState = rememberLazyListState()
    val coroutineScope = rememberCoroutineScope()
    val pageIndexMap = remember(flaggedItems) {
        val map = mutableMapOf<String, Int>()
        var index = 0

        // Add "Overview" at index 0
        map[Constant.OVERVIEW_KEY] = index
        index++

        // Add "Flagged Items" at index 1
        map[Constant.FLAGGED_ITEM_KEY] = index
        index++

        // Add actual pages starting from index 2
        if (flaggedItems.isNotEmpty()) {
            flaggedItems.groupBy { it.pageTitle }.keys.forEach { page ->
                map[page] = index
                index++
            }
        }
        map
    }
    val navigationEventState = rememberNavigationEventState<NavigationEventInfo>(
        currentInfo = NavigationEventInfo.None,
    )

    fun handleOnClickPage(page: String) {
        showPageModal = false
        pageIndexMap[page]?.let { index ->
            coroutineScope.launch {
                listState.animateScrollToItem(index)
            }
        }
    }

    NavigationBackHandler(state = navigationEventState, onBackCompleted = {
        onBack()
    })

    LaunchedEffect(Unit) {
        viewModel.navigationEvent.collect { event ->
            when (event) {
                is InspectionCompleteEvent.NavigateToLanding -> onApproved(event.inspectionId)
                is InspectionCompleteEvent.NavigateToInspectionAnswer -> onEdit()
            }
        }
    }

    Box(
        modifier = Modifier.fillMaxSize()
            .background(color.bg.normal)
            .padding(top = paddingValues.calculateTopPadding())
    ) {
        Column(
            modifier = Modifier.fillMaxSize()
        ) {
            Header(
                showNavigate = flaggedItems.isNotEmpty(),
                showPageModal = showPageModal,
                onBack = onBack,
                onEdit = {
                    state.onEdit()
                },
                onShowConfirmApproval = {
                    showApprovalModal = true
                },
                onShowPageModal = {
                    showPageModal = true
                },
                onDownloadPdf = {
                    state.onDownloadReport()
                }
            )

            InspectionCompleteContent(
                data = data,
                flaggedItems = flaggedItems,
                templateOptions = templateOptions,
                listState = listState,
                paddingValues = paddingValues,
                onClickPage = { handleOnClickPage(it) }
            )
        }

        PageInfoModal(
            show = showPageModal,
            pages = pages,
            onDismiss = {
                showPageModal = false
            },
            onClick = { page ->
                handleOnClickPage(page)
            }
        )

        ConfirmApprovalModal(
            show = showApprovalModal,
            gradientColors = listOf(
                Color(0xFF629DFF).copy(alpha = 0.6f),
                Color(0xFFFFFFFF)
            ),
            onDismiss = {
                showApprovalModal = false
            },
            onConfirm = {
                showApprovalModal = false
                state.onApprove()
            }
        )

        LoadingDialog(state.isLoading)
    }
}
